import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { faCalendarRange, faUser } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import dayjs from 'dayjs';
import { useEffect, useRef, useState } from 'react';
import { StyleProps } from '@/domain/mitarbeiter/features/rotationsplan/features/Kalender/desktop/components/station/types';
import { faCircle } from '@fortawesome/pro-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import { useRotationsplanFeatureContext } from '@/domain/mitarbeiter/features/rotationsplan/features/shared/RotationsplanFeatureContext';
import { KalenderStationByMitarbeiter } from '@/domain/mitarbeiter/features/rotationsplan/features/Kalender/entities/KalenderStation';

/* eslint-disable max-lines-per-function */
export function MitarbeiterKalenderStation({
  kalenderStation,
  displayYear,
}: {
  kalenderStation: KalenderStationByMitarbeiter;
  displayYear: number;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const {
    store: {
      ui: {
        kalender: { isScrolling },
      },
    },
  } = useRotationsplanFeatureContext();
  const [open, setOpen] = useState(false);
  const [cursor, setCursor] = useState({ x: 0 });
  const { t } = useTranslation();

  const handleMouseEnter = (event: React.MouseEvent) => {
    setOpen(true);
    setCursor({
      x: event.clientX - ref.current.getBoundingClientRect().left,
    });
  };

  const handleMouseLeave = () => {
    setOpen(false);
  };

  useEffect(() => {
    if (isScrolling && open) {
      setOpen(false);
    }
  }, [isScrolling, open]);

  return (
    <div
      className={`col-start-(--col-start) col-end-(--col-end) p-1`}
      style={
        {
          '--col-start': kalenderStation.getColStart(displayYear),
          '--col-end': kalenderStation.getColEnd(displayYear),
        } as StyleProps
      }>
      <div className="relative w-full h-full" ref={ref}>
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger
            style={{
              position: 'absolute',
              left: cursor.x,
              bottom: 0,
              height: 1,
              width: 1,
              color: 'transparent',
            }}
          />
          <div
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            className={`w-full h-full bg-(--bg-color) border-(--border-color) border border-solid rounded-md flex items-center justify-start p-2`}
            style={
              {
                '--bg-color': kalenderStation.arbeitsbereich.getFillColor(),
                '--border-color':
                  kalenderStation.arbeitsbereich.getBorderColor(),
              } as StyleProps
            }>
            <div className="text-xs truncate">
              {kalenderStation.arbeitsbereich.name}
            </div>
            <PopoverContent className="bg-white flex flex-col gap-2 p-3 text-sm w-75">
              <div className="flex gap-2 items-center">
                <FontAwesomeIcon
                  icon={faCircle}
                  color={kalenderStation.arbeitsbereich.getBorderColor()}
                />
                <div className="font-semibold">
                  {kalenderStation.arbeitsbereich.name}
                </div>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <FontAwesomeIcon icon={faCalendarRange} />
                  {t('domain.mitarbeiter.rotationsplan.zeitraum')}
                </div>
                <div className="font-semibold">
                  {dayjs(kalenderStation.station.von).format('DD.MM') +
                    ' – ' +
                    dayjs(kalenderStation.station.bis).format('DD.MM.YYYY')}
                </div>
              </div>
              {kalenderStation.station.bereichsleiter && (
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <FontAwesomeIcon icon={faUser} />
                    {t('domain.mitarbeiter.rotationsplan.bereichsleitung')}
                  </div>
                  <div className="font-semibold">
                    {kalenderStation.station.bereichsleiter.vorname +
                      ' ' +
                      kalenderStation.station.bereichsleiter.nachname}
                  </div>
                </div>
              )}
            </PopoverContent>
          </div>
        </Popover>
      </div>
    </div>
  );
}
